/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { bool } from '@ember/object/computed';
import { isBlank } from '@ember/utils';
import DS from 'ember-data';
import { task } from 'ember-concurrency';

import CollectionModel from 'cio/models/collection';
import FormValidations from 'cio/utils/form-validations';
import OAuthService from 'cio/services/oauth';
import ApiErrorsService from 'cio/services/api-errors';
import GoogleSheetsService from 'cio/services/google-sheets';
import { SheetObject } from 'cio/utils/google-sheets';
import isDestroyed from 'cio/utils/is-destroyed';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const Validations = {
  name(this: CollectionUpdateModal, value: string, _model: CollectionModel) {
    if (isBlank(value)) {
      return 'Name cannot be blank';
    }
    if (this.collectionNames?.includes(value)) {
      return 'Collection names must be unique';
    }

    return true;
  },
  import: {
    dependentKeys: ['s3Import', 'sheet', 'url'],
    validator(_value: string, model: CollectionModel) {
      let importAttrs: ('s3Import' | 'sheet' | 'url')[] = ['s3Import', 'sheet', 'url'];

      if (importAttrs.every((attr) => isBlank(model[attr]))) {
        return 'A file must be imported';
      }

      return true;
    },
  },
};

type CollectionUpdateModalArgs = {
  collection?: CollectionModel;
  collections?: CollectionModel[];
  onClose: () => unknown;
  onSubmit: (collection: CollectionModel) => unknown;
};

export default class CollectionUpdateModal extends Component<CollectionUpdateModalArgs> {
  @service store!: DS.Store;
  @service oauth!: OAuthService;
  @service apiErrors!: ApiErrorsService;
  @service googleSheets!: GoogleSheetsService;

  @tracked collectionNames?: string[];
  @tracked activeCollection!: CollectionModel;
  @tracked validations!: FormValidations;
  @bool('collection') editing!: boolean;

  acceptedFileExtensions = ['csv', 'json'];
  maxFileSize = MAX_FILE_SIZE;

  get title() {
    return this.activeCollection.get('isNew') ? 'Add a new Collection' : `Update ${this.activeCollection.name}`;
  }

  get needToGrantAdditionalScopes() {
    return this.googleSheets.needToGrantAdditionalScopes && this.args.collection?.updateType === 'sheet';
  }

  get needToReauthorize() {
    return this.googleSheets.needToReauthorize && this.args.collection?.updateType === 'sheet';
  }

  submitTask = task({ drop: true }, async (collection: CollectionModel, event: Event) => {
    event.preventDefault();

    if (collection.updateType == null) {
      collection.set('updateType', collection.sheet != null ? 'sheet' : 's3_import');
    }

    if (collection.sheet != null) {
      await this.googleSheets.getUserAccessToken.perform();

      if (this.googleSheets.accessToken != null) {
        await this.googleSheets.checkTokenScopes.perform();
      }
    }

    if (!this.googleSheets.hasRequiredScopes || !this.googleSheets.hasAuthorizedGoogleDrive) {
      return;
    }

    await this.validations.validateModel();
    await this.args.onSubmit(collection);
    this.activeCollection.resetUpdateType();
  });

  @action
  csvUpload({ filename, key }: { filename: string; key: string }) {
    this.activeCollection.setProperties({
      filename,
      updateType: 's3_import',
      s3Import: key,
      sheet: null,
    });
    this.validations.validateAttribute('import');
  }

  @action
  googleSheetImport({ sheetId, sheetName, spreadsheetId, spreadsheetName }: SheetObject) {
    this.activeCollection.setProperties({
      updateType: 'sheet',
      sheet: { sheetId, sheetName, spreadsheetId, spreadsheetName },
    });
    this.validations.validateAttribute('import');
  }

  @action
  didDeleteFile() {
    this.activeCollection.setProperties({
      updateType: null,
      filename: null,
      s3Import: null,
      sheet: null,
    });
  }

  @action
  onNameChange(name: string) {
    this.activeCollection.set('name', name);
    this.validations.validateAttribute('name');
  }

  constructor(owner: unknown, args: CollectionUpdateModalArgs) {
    super(owner, args);

    this.collectionNames = this.args.collections?.map((collection) => collection.name);

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'activeCollection',
    });

    this.activeCollection = args.collection ?? this.store.createRecord('collection');
  }

  willDestroy() {
    super.willDestroy();

    if (isDestroyed(this.activeCollection)) {
      return;
    }

    if (this.activeCollection.get('isNew')) {
      this.activeCollection.unloadRecord();
    } else {
      this.activeCollection.rollbackAttributes();
    }
  }
}
